/**
 * Die Primäraktion und ihre Geschwister.
 *
 * Übernommen aus dem Design-System-Projekt (`components/forms/Button.jsx`),
 * übersetzt nach TypeScript. Maße, Radien und Bewegungsdauern sind unverändert;
 * die Farben kommen aus den CSS-Variablen und damit aus `tokens.ts`.
 *
 * **Nur die Markenfarbe.** Ein Knopf ist Navigation, keine Datenaussage — eine
 * Größen- oder Spartenfarbe hat hier nichts zu suchen (ADR 0013).
 */
import { useState, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from "react";
import { fontWeight } from "../tokens.js";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Über die volle Breite — für die Aktion eines Formulars. */
  readonly block?: boolean;
  readonly leadingIcon?: ReactNode;
  readonly type?: "button" | "submit" | "reset";
}

const SIZES: Record<ButtonSize, { height: number; padding: string; fontSize: number; gap: number }> =
  {
    sm: { height: 30, padding: "0 10px", fontSize: 12.5, gap: 6 },
    md: { height: 34, padding: "0 14px", fontSize: 13.5, gap: 7 },
    lg: { height: 40, padding: "0 18px", fontSize: 14.5, gap: 8 },
  };

const VARIANTS: Record<ButtonVariant, CSSProperties> = {
  primary: {
    background: "var(--hm-green-500)",
    color: "var(--text-invert)",
    border: "1px solid var(--hm-green-500)",
    boxShadow: "var(--shadow-brand)",
  },
  secondary: {
    background: "var(--surface-card)",
    color: "var(--text-strong)",
    border: "1px solid var(--border-default)",
    boxShadow: "var(--shadow-xs)",
  },
  ghost: {
    background: "transparent",
    color: "var(--text-body)",
    border: "1px solid transparent",
  },
  danger: {
    background: "var(--hm-danger)",
    color: "var(--text-invert)",
    border: "1px solid var(--hm-danger)",
  },
};

export function Button({
  children,
  variant = "primary",
  size = "md",
  block = false,
  disabled = false,
  leadingIcon,
  type = "button",
  style,
  ...rest
}: ButtonProps) {
  const [hovered, setHovered] = useState(false);
  const metrics = SIZES[size];

  return (
    <button
      type={type}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: block ? "flex" : "inline-flex",
        width: block ? "100%" : "auto",
        alignItems: "center",
        justifyContent: "center",
        gap: metrics.gap,
        height: metrics.height,
        padding: metrics.padding,
        fontFamily: "var(--font-body)",
        fontSize: metrics.fontSize,
        fontWeight: fontWeight.semibold,
        lineHeight: 1,
        letterSpacing: "var(--tracking-snug)",
        borderRadius: size === "lg" ? "var(--radius-md)" : "var(--radius-sm)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: "filter .15s ease, box-shadow .15s ease",
        filter: hovered && !disabled ? "brightness(1.05)" : "none",
        ...VARIANTS[variant],
        // Der Markenschatten liegt ausschließlich unter der Primäraktion — und
        // nicht unter einer, die gerade nicht auslösbar ist.
        ...(disabled ? { boxShadow: "none" } : {}),
        ...style,
      }}
      {...rest}
    >
      {leadingIcon !== undefined && (
        <span style={{ display: "inline-flex", width: "1.05em", height: "1.05em" }}>
          {leadingIcon}
        </span>
      )}
      {children}
    </button>
  );
}
